import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

// App-owned sessionStorage keys that hold per-user/session data.
const SESSION_KEYS = ['user', 'usertoken'];

// App-owned localStorage keys that hold per-user/transient data: role
// selection, the registration-flow identifiers (reg_*), the cached
// business-type/notifications, and anything else set outside of the
// Auth0 SDK's own token cache. Keeping this list exhaustive matters —
// any key left behind here is visible to the *next* account that logs
// in on the same browser (e.g. NotificationButton reads `notifications`
// for whichever account happens to be signed in).
const LOCAL_KEYS = [
  'role',
  'email',
  'token',
  'businessType',
  'notifications',
  'reg_partner_id',
  'reg_partner_owner',
  'reg_shop_id',
  'reg_shop_owner_id',
  'reg_customer_id',
  'reg_auth0_user_id',
  'reg_owner_email',
  'reg_owner_firstName',
  'reg_owner_lastName',
  'reg_owner_title',
  'reg_owner_phone',
];

/**
 * Shared logout handler for the authenticated app shell's navigation
 * components (desktop `SideNav`, the responsive/mobile `SideMenu`, and
 * any code path that needs to forcibly end an Auth0 session, e.g. a
 * failed role/partner lookup). Clears only the app's own session/local
 * storage keys — not the Auth0 SDK's own cached tokens, which `logout()`
 * needs to build a valid federated logout request — and ends the Auth0
 * session (SSO cookie) so a subsequent login doesn't silently
 * re-authenticate the same user.
 *
 * Returns a function that optionally takes a `returnTo` path (relative
 * to the app origin) to send the user to once Auth0's logout redirect
 * completes. Must be registered in Auth0's "Allowed Logout URLs".
 * Defaults to the app origin ('/').
 */
export const useLogout = (): ((returnTo?: string) => void) => {
  const { logout } = useAuth0();

  return useCallback(
    (returnTo?: string): void => {
      // Marks a logout as in-flight before the storage keys below (in
      // particular `role`) are cleared. useHydratedSession runs as
      // multiple independent hook instances (App.tsx's route guard,
      // Auth.tsx's callback page) that each re-derive readiness from
      // localStorage/sessionStorage. Without this marker, clearing
      // `role` here — synchronously, before the browser actually
      // navigates away for the Auth0 logout redirect — makes a
      // *different* hook instance briefly believe there's nothing left
      // to hydrate and flip `ready` true, letting it race ahead (e.g.
      // Auth.tsx navigating to /dashboard) before the real redirect
      // lands. useHydratedSession checks this flag and pauses instead.
      sessionStorage.setItem('authLoggingOut', '1');
      SESSION_KEYS.forEach((key) => sessionStorage.removeItem(key));
      LOCAL_KEYS.forEach((key) => localStorage.removeItem(key));

      logout({
        returnTo: returnTo
          ? `${window.location.origin}${returnTo}`
          : window.location.origin,
      });
    },
    [logout]
  );
};
